/**
 * Driver auth screens — LoginScreen, SetupScreen, ForgotScreen.
 * Extracted from DriverApp.jsx (V2-03.12e).
 * No auth logic changes — behavior-identical to inline originals.
 */

import React, { useState, useRef } from 'react';
import { C, FONT, GLASS_STRONG, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { apiPost, setToken } from './driver-session.js';

// ============================================================================
// Numpad Component — touch-friendly numeric input (auth-only)
// ============================================================================

interface NumpadProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  label?: string;
  onEnter?: () => void;
}

function Numpad({ value, onChange, maxLength, label, onEnter }: NumpadProps) {
  var keys = ["1","2","3","4","5","6","7","8","9","","0","\u232b"];
  var inputRef = useRef<HTMLInputElement>(null);
  function handleKeyboard(e: React.KeyboardEvent) {
    var ch = e.key;
    if (/^[0-9]$/.test(ch) && value.length < (maxLength || 10)) { onChange(value + ch); }
    else if (ch === "Backspace") { onChange(value.slice(0, -1)); }
    else if (ch === "Enter" && onEnter) { onEnter(); }
  }
  return React.createElement("div", { style: { textAlign: "center" } },
    label && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 8 } }, label),
    React.createElement("input", {
      ref: inputRef, autoFocus: true, inputMode: "numeric", pattern: "[0-9]*",
      value: value, readOnly: true,
      onKeyDown: handleKeyboard,
      style: { position: "absolute", opacity: 0, width: 1, height: 1, pointerEvents: "none" },
      "aria-label": label || "Numeric input"
    }),
    React.createElement("div", {
      style: { fontFamily: FONT.mono, fontSize: 32, color: C.gold, letterSpacing: 4, marginBottom: 16, minHeight: 48, cursor: "text" },
      onClick: function() { if (inputRef.current) inputRef.current.focus(); }
    }, value || "\u00A0"),
    React.createElement("div", { style: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, maxWidth: 280, margin: "0 auto" } },
      keys.map(function(k, i) {
        if (k === "") return React.createElement("div", { key: i });
        return React.createElement("button", {
          key: i,
          type: "button",
          style: { padding: 16, fontSize: 22, fontFamily: FONT.mono, background: "rgba(255,255,255,0.04)", color: C.text, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, cursor: "pointer", minHeight: 56, touchAction: "manipulation", WebkitUserSelect: "none", userSelect: "none" },
          onClick: function() {
            if (k === "⌫") { onChange(value.slice(0, -1)); }
            else if (value.length < (maxLength || 10)) { onChange(value + k); }
            if (inputRef.current) inputRef.current.focus();
          }
        }, k);
      })
    )
  );
}

// ============================================================================
// LoginScreen
// ============================================================================

interface LoginScreenProps {
  onValidated: (target: string, id: string, name: string) => void;
  onForgot: (id: string) => void;
}

export function LoginScreen({ onValidated, onForgot }: LoginScreenProps) {
  var [driverId, setDriverId] = useState("");
  var [password, setPassword] = useState("");
  var [step, setStep] = useState("ID"); // ID, PASSWORD
  var [name, setName] = useState("");
  var [error, setError] = useState("");
  var [loading, setLoading] = useState(false);
  var [showPw, setShowPw] = useState(false);

  function handleValidate() {
    if (driverId.length !== 7) return;
    setLoading(true); setError("");
    apiPost<{ valid?: boolean; needs_setup?: boolean; first_name?: string; message?: string }>("/api/auth/validate", { driver_id: driverId }).then(function(data) {
      setLoading(false);
      if (data.valid) {
        if (data.needs_setup) { onValidated("SETUP", driverId, data.first_name || ""); return; }
        setName(data.first_name || "");
        setStep("PASSWORD");
      } else {
        setError(data.message || "Driver ID not found");
      }
    });
  }

  function handleLogin() {
    if (!password) return;
    setLoading(true); setError("");
    apiPost<{ success?: boolean; token?: string; employee?: { first_name?: string; [k: string]: unknown }; message?: string; needs_setup?: boolean }>("/api/auth/login", { driver_id: driverId, password: password }).then(function(data) {
      setLoading(false);
      if (data.success) {
        setToken(data.token || "");
        localStorage.setItem("pwd_driver_employee", JSON.stringify(data.employee));
        onValidated("HOME", driverId, data.employee?.first_name || "");
      } else {
        setError(data.message || "Login failed");
        if (data.needs_setup) { onValidated("SETUP", driverId, name); }
      }
    });
  }

  return React.createElement("div", { style: { minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
    React.createElement("img", { src: "../assets/images/pwd_logo.png", style: { width: 120, marginBottom: 24 }, alt: "PWD" }),
    React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 22, color: C.text, textAlign: "center", marginBottom: 4 } }, "Driver Portal"),
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver, textAlign: "center", marginBottom: 24 } }, step === "ID" ? "Enter your 7-digit Driver ID" : "Welcome back, " + name),

      step === "ID" ? React.createElement("div", null,
        React.createElement(Numpad, { value: driverId, onChange: setDriverId, maxLength: 7, label: "Driver ID", onEnter: handleValidate }),
        React.createElement("div", { style: { marginTop: 20 } },
          React.createElement("button", {
            type: "button",
            style: driverId.length === 7 ? BTN : BTN_DISABLED,
            disabled: driverId.length !== 7 || loading,
            onClick: handleValidate
          }, loading ? "Checking..." : "Continue")
        )
      ) : React.createElement("div", null,
        React.createElement("div", { style: { position: "relative", marginBottom: 16 } },
          React.createElement("input", {
            type: showPw ? "text" : "password",
            style: INPUT,
            placeholder: "Password",
            value: password,
            onChange: function(e) { setPassword(e.target.value); },
            onKeyDown: function(e) { if (e.key === "Enter") handleLogin(); },
            autoFocus: true
          }),
          React.createElement("button", {
            style: { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 12, fontFamily: FONT.mono },
            onClick: function() { setShowPw(!showPw); }
          }, showPw ? "HIDE" : "SHOW")
        ),
        React.createElement("button", { style: loading ? BTN_DISABLED : BTN, disabled: !password || loading, onClick: handleLogin }, loading ? "Signing in..." : "Sign In"),
        React.createElement("div", { style: { textAlign: "center", marginTop: 12 } },
          React.createElement("button", {
            style: { background: "none", border: "none", color: C.gold, fontSize: 13, fontFamily: FONT.mono, cursor: "pointer", textDecoration: "underline" },
            onClick: function() { onForgot(driverId); }
          }, "Forgot Password?")
        )
      ),

      error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginTop: 12 } }, error),

      step === "PASSWORD" && React.createElement("div", { style: { textAlign: "center", marginTop: 12 } },
        React.createElement("button", {
          style: { background: "none", border: "none", color: C.textMuted, fontSize: 12, fontFamily: FONT.mono, cursor: "pointer" },
          onClick: function() { setStep("ID"); setPassword(""); setError(""); }
        }, "\u2190 Different ID")
      )
    )
  );
}

// ============================================================================
// SetupScreen
// ============================================================================

interface SetupScreenProps {
  driverId: string;
  firstName: string;
  onComplete: () => void;
  onBack: () => void;
}

export function SetupScreen({ driverId, firstName, onComplete, onBack }: SetupScreenProps) {
  var [password, setPassword] = useState("");
  var [confirm, setConfirm] = useState("");
  var [error, setError] = useState("");
  var [loading, setLoading] = useState(false);
  var [showPw, setShowPw] = useState(false);

  var rules = [
    { label: "8+ characters", ok: password.length >= 8 },
    { label: "Uppercase", ok: /[A-Z]/.test(password) },
    { label: "Lowercase", ok: /[a-z]/.test(password) },
    { label: "Number", ok: /[0-9]/.test(password) },
    { label: "Special (!@#$%^&*)", ok: /[!@#$%^&*]/.test(password) },
  ];
  var allValid = rules.every(function(r) { return r.ok; }) && password === confirm && confirm.length > 0;

  function handleSetup() {
    if (!allValid) return;
    setLoading(true); setError("");
    apiPost<{ success?: boolean; error?: string }>("/api/auth/setup", { driver_id: driverId, password: password, confirm: confirm }).then(function(data) {
      setLoading(false);
      if (data.success) { onComplete(); }
      else { setError(data.error || "Setup failed"); }
    });
  }

  return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
    React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, textAlign: "center", marginBottom: 4 } }, "Set Your Password"),
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver, textAlign: "center", marginBottom: 20 } }, "Hi " + firstName + "! Create a secure password."),
      React.createElement("input", { type: showPw ? "text" : "password", style: { ...INPUT, marginBottom: 12 }, placeholder: "New password", value: password, onChange: function(e) { setPassword(e.target.value); } }),
      React.createElement("input", { type: showPw ? "text" : "password", style: { ...INPUT, marginBottom: 12 }, placeholder: "Confirm password", value: confirm, onChange: function(e) { setConfirm(e.target.value); } }),
      React.createElement("button", { style: { background: "none", border: "none", color: C.textMuted, fontSize: 11, fontFamily: FONT.mono, cursor: "pointer", marginBottom: 12 }, onClick: function() { setShowPw(!showPw); } }, showPw ? "Hide passwords" : "Show passwords"),
      React.createElement("div", { style: { marginBottom: 16 } },
        rules.map(function(r) {
          return React.createElement("div", { key: r.label, style: { fontFamily: FONT.mono, fontSize: 11, color: r.ok ? C.green : C.textMuted, marginBottom: 2 } }, (r.ok ? "\u2713 " : "\u2022 ") + r.label);
        }),
        confirm.length > 0 && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: password === confirm ? C.green : C.red, marginTop: 4 } }, password === confirm ? "\u2713 Passwords match" : "\u2717 Passwords don't match")
      ),
      React.createElement("button", { style: allValid ? BTN : BTN_DISABLED, disabled: !allValid || loading, onClick: handleSetup }, loading ? "Setting up..." : "Set Password"),
      error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginTop: 12 } }, error),
      React.createElement("div", { style: { textAlign: "center", marginTop: 12 } },
        React.createElement("button", { style: { background: "none", border: "none", color: C.textMuted, fontSize: 12, fontFamily: FONT.mono, cursor: "pointer" }, onClick: onBack }, "\u2190 Back")
      )
    )
  );
}

// ============================================================================
// ForgotScreen
// ============================================================================

interface ForgotScreenProps {
  driverId: string;
  onBack: () => void;
}

// ============================================================================
// ResetPasswordScreen — handles email reset link with ?token=TOKEN
// ============================================================================

interface ResetPasswordScreenProps {
  token: string;
  onComplete: () => void;
  onBack: () => void;
}

export function ResetPasswordScreen({ token, onComplete, onBack }: ResetPasswordScreenProps) {
  var [password, setPassword] = useState("");
  var [confirm, setConfirm] = useState("");
  var [error, setError] = useState("");
  var [loading, setLoading] = useState(false);
  var [showPw, setShowPw] = useState(false);
  var [invalidState, setInvalidState] = useState<string | null>(null); // 'expired' | 'used' | null

  var rules = [
    { label: "8+ characters", ok: password.length >= 8 },
    { label: "Uppercase", ok: /[A-Z]/.test(password) },
    { label: "Lowercase", ok: /[a-z]/.test(password) },
    { label: "Number", ok: /[0-9]/.test(password) },
    { label: "Special (!@#$%^&*)", ok: /[!@#$%^&*]/.test(password) },
  ];
  var allValid = rules.every(function(r) { return r.ok; }) && password === confirm && confirm.length > 0;

  function handleReset() {
    if (!allValid) return;
    setLoading(true); setError("");
    apiPost<{ success?: boolean; expired?: boolean; used?: boolean; error?: string }>("/api/auth/reset-password", { token: token, password: password, confirm: confirm }).then(function(data) {
      setLoading(false);
      if (data.success) {
        onComplete();
      } else if (data.expired) {
        setInvalidState("expired");
      } else if (data.used) {
        setInvalidState("used");
      } else {
        setError(data.error || "Error resetting password.");
      }
    }).catch(function() {
      setLoading(false);
      setError("Connection error. Please try again.");
    });
  }

  if (invalidState) {
    return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
      React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20, textAlign: "center" } },
        React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, marginBottom: 16 } }, "Reset Link Invalid"),
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 20, lineHeight: 1.6 } },
          invalidState === "expired"
            ? "This reset link has expired. Please request a new one."
            : "This reset link has already been used."
        ),
        React.createElement("button", { style: BTN, onClick: onBack }, "Back to Login")
      )
    );
  }

  return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
    React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, textAlign: "center", marginBottom: 4 } }, "Reset Your Password"),
      React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 12, color: C.silver, textAlign: "center", marginBottom: 20 } }, "Enter a new secure password below."),
      React.createElement("input", { type: showPw ? "text" : "password", style: { ...INPUT, marginBottom: 12 }, placeholder: "New password", value: password, onChange: function(e: React.ChangeEvent<HTMLInputElement>) { setPassword(e.target.value); } }),
      React.createElement("input", { type: showPw ? "text" : "password", style: { ...INPUT, marginBottom: 12 }, placeholder: "Confirm password", value: confirm, onChange: function(e: React.ChangeEvent<HTMLInputElement>) { setConfirm(e.target.value); }, onKeyDown: function(e: React.KeyboardEvent) { if (e.key === "Enter" && allValid) handleReset(); } }),
      React.createElement("button", { style: { background: "none", border: "none", color: C.textMuted, fontSize: 11, fontFamily: FONT.mono, cursor: "pointer", marginBottom: 12 }, onClick: function() { setShowPw(!showPw); } }, showPw ? "Hide passwords" : "Show passwords"),
      React.createElement("div", { style: { marginBottom: 16 } },
        rules.map(function(r) {
          return React.createElement("div", { key: r.label, style: { fontFamily: FONT.mono, fontSize: 11, color: r.ok ? C.green : C.textMuted, marginBottom: 2 } }, (r.ok ? "\u2713 " : "\u2022 ") + r.label);
        }),
        confirm.length > 0 && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: password === confirm ? C.green : C.red, marginTop: 4 } }, password === confirm ? "\u2713 Passwords match" : "\u2717 Passwords don't match")
      ),
      React.createElement("button", { style: allValid && !loading ? BTN : BTN_DISABLED, disabled: !allValid || loading, onClick: handleReset }, loading ? "Resetting..." : "Reset Password"),
      error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginTop: 12 } }, error),
      React.createElement("div", { style: { textAlign: "center", marginTop: 12 } },
        React.createElement("button", { style: { background: "none", border: "none", color: C.textMuted, fontSize: 12, fontFamily: FONT.mono, cursor: "pointer" }, onClick: onBack }, "\u2190 Back to Login")
      )
    )
  );
}

// ============================================================================
// ForgotScreen
// ============================================================================

export function ForgotScreen({ driverId: initId, onBack }: ForgotScreenProps) {
  var [driverId, setDriverId] = useState(initId || "");
  var [sent, setSent] = useState(false);
  var [error, setError] = useState("");
  var [loading, setLoading] = useState(false);

  function handleSubmit() {
    if (driverId.length !== 7) return;
    setLoading(true); setError("");
    apiPost<{ success?: boolean; no_email?: boolean; message?: string }>("/api/auth/forgot-password", { driver_id: driverId }).then(function(data) {
      setLoading(false);
      if (data.success) { setSent(true); }
      else if (data.no_email) { setError("No email on file. See your manager."); }
      else { setError(data.message || "Request failed"); }
    });
  }

  return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
    React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20, textAlign: "center" } },
      React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, marginBottom: 16 } }, sent ? "Check Your Email" : "Forgot Password"),
      sent ? React.createElement("div", null,
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 20 } }, "A password reset link has been sent to the email on file."),
        React.createElement("button", { style: BTN, onClick: onBack }, "Back to Login")
      ) : React.createElement("div", null,
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 16 } }, "Enter your Driver ID and we'll send a reset link."),
        React.createElement(Numpad, { value: driverId, onChange: setDriverId, maxLength: 7, label: "Driver ID", onEnter: handleSubmit }),
        React.createElement("div", { style: { marginTop: 16 } },
          React.createElement("button", { style: driverId.length === 7 ? BTN : BTN_DISABLED, disabled: driverId.length !== 7 || loading, onClick: handleSubmit }, loading ? "Sending..." : "Send Reset Link")
        ),
        error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, marginTop: 12 } }, error),
        React.createElement("div", { style: { marginTop: 12 } },
          React.createElement("button", { style: { background: "none", border: "none", color: C.textMuted, fontSize: 12, fontFamily: FONT.mono, cursor: "pointer" }, onClick: onBack }, "\u2190 Back")
        )
      )
    )
  );
}
